import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ClockIcon, type LucideIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Shared status card for a single Global OSINT credential source. Driven by a
 * plain `{ id, label, description, Icon }` descriptor plus a `configured`
 * boolean, so both `HumanIntelligenceCredentialsPanel` (Hunter.io / Lusha /
 * Apollo / Dehashed) and `InfrastructureCredentialsPanel` (Bright Data Proxy)
 * render the identical card + Edit-dialog UX backed by the enrichment master-key
 * endpoints (`/enrichment/{plugin}/key`).
 *
 * The brand logo is a flat, theme-agnostic PNG at `/icons/{id}.png` (color logos
 * that look the same in light and dark); on load error it falls back to the
 * descriptor's lucide `Icon`. There is NO enable/disable toggle and NO credits
 * line — for these creds, configured == active.
 */

export interface InfraCredentialSource {
  id: string;
  label: string;
  description: string;
  Icon: LucideIcon;
  /**
   * Optional short caveat rendered as a small warning line under the
   * description (e.g. Bright Data acceptable-use scope). Omit for sources with
   * no caveat — HUMINT cards leave this unset.
   */
  warning?: string;
  /**
   * When true the card is a non-interactive "COMING SOON" placeholder: it shows
   * a muted coming-soon chip instead of the Configured/Not-Configured status and
   * renders no Edit button, so the config dialog can't be opened. The icon, name,
   * and description render as normal. Defaults off — leave unset for a fully
   * configurable source. Mirrors the `comingSoon` treatment in
   * `CollectionRunWizard`/`IntegrationsWizard`.
   */
  comingSoon?: boolean;
}

export function InfraCredentialCard({
  source,
  configured,
  onEdit,
}: {
  source: InfraCredentialSource;
  configured: boolean;
  onEdit: () => void;
}) {
  const { Icon, comingSoon } = source;
  const [iconError, setIconError] = useState(false);
  const statusLabel = comingSoon
    ? 'coming soon'
    : configured
      ? 'configured'
      : 'not configured';

  return (
    <article
      aria-label={`${source.label} - ${statusLabel}`}
      className="border-chariot-stroke bg-chariot-background-primary flex flex-col rounded-lg border transition-colors"
    >
      <div className="flex flex-col p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {iconError ? (
                <Icon className="text-chariot-text-secondary size-5" />
              ) : (
                <img
                  src={`/icons/${source.id}.png`}
                  alt=""
                  className="size-5"
                  onError={() => setIconError(true)}
                />
              )}
              <h3 className="text-chariot-text-primary text-sm font-medium">
                {source.label}
              </h3>
            </div>
            <p className="text-chariot-text-secondary mt-2 text-xs">
              {source.description}
            </p>
            {source.warning && (
              <p className="text-chariot-stroke-high mt-2 text-xs">
                {source.warning}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {comingSoon ? (
              // Non-configurable placeholder: muted coming-soon chip, no Edit
              // button, so the config dialog can't be opened. Matches the
              // "COMING SOON" idiom in CollectionRunWizard/IntegrationsWizard.
              <span className="flex items-center gap-1.5">
                <span className="border-chariot-stroke text-chariot-text-disabled rounded-full border px-2 py-0.5 text-[9.5px] font-extrabold tracking-wide">
                  COMING SOON
                </span>
                <ClockIcon className="text-chariot-text-disabled size-4 shrink-0" />
              </span>
            ) : (
              <>
                {configured ? (
                  <span className="text-success flex items-center gap-1 text-xs">
                    <CheckCircleIcon className="size-4" />
                    Configured
                  </span>
                ) : (
                  <span className="text-chariot-status-warning flex items-center gap-1 text-xs">
                    <XCircleIcon className="size-4" />
                    Not Configured
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onEdit}
                  aria-label={`Edit ${source.label} configuration`}
                >
                  Edit
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
